"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import {
  enableOutboundCallingPhoneNumberAction,
  startOutgoingVoiceCallAction,
} from "@/server-actions/outgoing-voice-calls"
import type { EnabledOutboundPhoneNumber, OutgoingVoiceCallResult } from "@/lib/voice/outgoing-calls"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import { outgoingVoiceCallPhoneNumbersQueryKey } from "@/hooks/queries/outgoing-voice-calls"
import { pbxPhoneNumbersQueryKey } from "@/hooks/queries/pbx-phone-numbers"
import type { StartOutgoingVoiceCallInput } from "@/lib/voice/outgoing-call-validation"
import { useTranslations } from "next-intl"


type StartOutgoingVoiceCallMutationInput = StartOutgoingVoiceCallInput & {
  setErrors?: (errors: Record<string, unknown>) => void
}

export function useStartOutgoingVoiceCallMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsOutgoingVoiceCalls")

  return useMutation({
    mutationFn: async ({
      setErrors,
      ...input
    }: StartOutgoingVoiceCallMutationInput): Promise<OutgoingVoiceCallResult> => {
      const result = await withQueryHandling(startOutgoingVoiceCallAction(input), {
        loading: tUi("startingOutgoingCall"),
        success: tUi("outgoingCallStarted"),
        setErrors,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

export function useEnableOutboundCallingPhoneNumberMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsOutgoingVoiceCalls")

  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { phoneNumber: string }): Promise<EnabledOutboundPhoneNumber> => {
      const result = await withQueryHandling(enableOutboundCallingPhoneNumberAction(input), {
        loading: tUi("enablingOutboundCalling"),
        success: tUi("outboundCallingEnabled"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: outgoingVoiceCallPhoneNumbersQueryKey }),
        queryClient.invalidateQueries({ queryKey: pbxPhoneNumbersQueryKey }),
      ])
    },
  })
}
